import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { logout } from "../services/api";
import { useAuth } from "../services/auth-context";

/** FR-081: sign out, for any role. `POST /auth/logout` has existed server-side since Phase 2 —
 * until now nothing in the UI ever called it, so there was simply no way to sign out.
 *
 * Clears the client-side auth context as well as the server cookie, then sends the user to the
 * sign-in page they came from: a company member back to their own company's page (so the
 * company's branding and slug are preserved), a CO Member to the CO Member sign-in. */
export function SignOutButton() {
  const { t } = useTranslation();
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  if (!auth) return null;

  const target = auth.role === "member" ? `/company/${auth.slug}/login` : "/co-member/login";

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await logout();
    } catch {
      // Even if the server call fails (offline, expired session), still clear local state and
      // leave — the user asked to sign out, and stranding them signed-in would be worse. The
      // cookie is httpOnly with its own expiry, so this degrades safely.
    } finally {
      setAuth(null);
      navigate(target, { replace: true });
    }
  }

  return (
    <button
      type="button"
      onClick={handleSignOut}
      disabled={signingOut}
      className="rounded px-3 py-1 text-sm font-sans text-body hover:bg-surface-alt disabled:opacity-50"
    >
      {signingOut ? t("common.signingOut", "Signing out…") : t("common.signOut", "Sign out")}
    </button>
  );
}
